import React, { useEffect, useMemo, useRef, useState } from 'react';
import Casteller from './Casteller';
import TemporaryPersonCreateButton from './TemporaryPersonCreateButton';
import resetPersonSelection from './resetPersonSelection';
import { sortMatches } from './castellerSearch';
import './LlistaCastellers.css'

import { panToCaixa } from '../PissarraController';
import { isBrowser, isDesktop } from 'react-device-detect';
import { applyTimeZone, getSubdomain } from '../../../utils/utils';
import Pressable from '../../other/Pressable';

import SETTINGS from '../../../SETTINGS';
import BotoPestanyaUp from './botons/BotoPestanyaUp';
import BotoPestanyaDown from './botons/BotoPestanyaDown';

import moment from 'moment';
import { getPersistentItem, setPersistentItem } from '../../../utils/storage';
import {
    buildInitialShowEtiqueta,
    ensurePerfilVisible,
    getShowEtiquetaStorageKey,
    normalizePerfilKey,
    togglePerfilVisibility,
} from './showEtiquetaState';

const COLLA = getSubdomain();

const getConfirmatsPerfils = (filteredCastellers) => ([
    {
        perfil: 'Fitxats + arribant',
        castellers: filteredCastellers
            .filter(c => c['assistència'] === 1 || c['assistència'] === 2)
    },
    {
        perfil: 'Fitxats',
        castellers: filteredCastellers.filter(c => c['assistència'] === 2)
    },
    {
        perfil: 'Arribant',
        castellers: filteredCastellers.filter(c => c['assistència'] === 1)
    }
]);

function BotoShowBuscar({ showBuscar, setNameSearch, setShowBuscar, setIsFocused }) {
    const handleClick = () => {
        if (showBuscar) {
            setNameSearch('');
            setIsFocused(false);
        }
        setShowBuscar(!showBuscar);
    }

    return (
        <button type="button" onClick={handleClick}
            className={showBuscar ? 'casteller-search-close' : 'boto boto-search'}
            aria-label={showBuscar ? 'Tancar el cercador' : 'Obrir el cercador'}
            title={showBuscar ? 'Tancar el cercador' : 'Obrir el cercador'}
            aria-expanded={showBuscar}>
            {showBuscar ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" focusable="false">
                <path d="m6 6 12 12M18 6 6 18" />
            </svg> : <span aria-hidden="true">🔍</span>}
        </button>
    )
}

function LlistaCastellers(props) {
    const [panBackToSelected, setPanBackToSelected] = useState(false);
    const [showEtiqueta, setShowEtiqueta] = useState(() => {
        const storageKey = getShowEtiquetaStorageKey(props.name);
        const storedShowEtiqueta = getPersistentItem(storageKey);

        if (!storedShowEtiqueta) {
            return buildInitialShowEtiqueta(props.name);
        }

        try {
            return buildInitialShowEtiqueta(props.name, JSON.parse(storedShowEtiqueta));
        } catch (error) {
            console.warn(`[LlistaCastellers] Could not parse "${storageKey}"`, error);
            return buildInitialShowEtiqueta(props.name);
        }
    });

    const { tabs, pestanya, setPestanya, etiquetes, etiquetaUsers, isModel, triggerClick, caixaSelected, arribenTard, surtenAviat, nameSearch, setNameSearch, showBuscar, setShowBuscar, setIsFocused } = props;
    const searchRef = useRef(null)

    const activeSlide = props?.swiper?.realIndex === props?.slideNum

    useEffect(() => panToCaixa(props.caixaSelected, props.panzoom, isBrowser), [panBackToSelected])

    useEffect(() => {
        if (!activeSlide) return;
        // Keep the search draft while choosing a destination, even with matches.

        // Focus search when new caixa is selected
        if (caixaSelected !== -1 && activeSlide && isDesktop) {
            setTimeout(() => searchRef?.current?.focus({ preventScroll: true }), 100)
        }

        if (caixaSelected === -1 && !nameSearch) {
            setShowBuscar(false);
        }
    }, [caixaSelected, activeSlide, triggerClick])

    useEffect(() => {
        if (showBuscar && activeSlide) {
            searchRef?.current?.focus();
        }
    }, [showBuscar, activeSlide])

    useEffect(() => {
        if (nameSearch !== '') {
            setShowBuscar(true);
        }
    }, [nameSearch])

    useEffect(() => {
        setPersistentItem(
            getShowEtiquetaStorageKey(props.name),
            JSON.stringify(showEtiqueta)
        );
    }, [props.name, showEtiqueta]);

    const filteredCastellers = useMemo(() => 
        props.castellers
            // Filter by the props' filter
            .filter(c => !isModel && nameSearch === '' ? props.filter(c) : true)
            // Filter out non-castellers (c.casteller === 0)
            .filter(c => nameSearch === '' ? (props.name !== 'famílies' ? c.casteller !== 0 : c.casteller === 0) : true)
    , [props.castellers, isModel, nameSearch, props.filter, props.name]);

    const uniqueAssistVals = [...new Set(filteredCastellers
        // Get assistencia
        .map(c => c['assistència'])
    )]
        .sort((a, b) => a < b ? 1 : -1);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        }
    }

    const etiqueta = props.json?.[caixaSelected]?.etiqueta;
    const perfil = props.json?.[caixaSelected]?.perfil;
    const selectedPerfil = useMemo(() => {
        if (props.name !== 'etiquetes') {
            return perfil;
        }

        return etiquetes.find(({ id }) => String(id) === String(etiqueta))?.nom || perfil;
    }, [props.name, etiquetes, etiqueta, perfil]);

    useEffect(() => {
        setShowEtiqueta(prevState => ensurePerfilVisible(props.name, prevState, selectedPerfil));
    }, [props.name, selectedPerfil])

    if (!activeSlide) return null;

    return (
        <>
            <header className="casteller-list-header">
                <div className={`casteller-list-toolbar${showBuscar ? ' searching' : ''}`}>
                    { !showBuscar && <div className='llista-name'><span>{(props.name || '').toUpperCase()}</span></div> }

                    { !showBuscar && <BotoPestanyaUp full={false} tabs={tabs} pestanya={pestanya} setPestanya={setPestanya} {...props} /> }
                    { !showBuscar && <BotoPestanyaDown full={false} tabs={tabs} pestanya={pestanya} setPestanya={setPestanya} {...props} /> }

                    {showBuscar &&
                        <input
                            ref={searchRef}
                            className="casteller-search-input"
                            aria-label="Cerca castellers"
                            placeholder='Busca a una persona...'
                            value={nameSearch}
                            onInput={e => setNameSearch(e.target.value)}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                            onKeyDown={handleKeyPress}
                        />
                    }

                    {!isModel && showBuscar && props.createTemporaryPerson &&
                        <TemporaryPersonCreateButton name={nameSearch} eventName={props.selectedEventInfo?.title}
                            canCreate={caixaSelected !== -1 && !props.posicions?.loading}
                            onCreate={name => {
                                if (props.createTemporaryPerson(name)) resetPersonSelection(props);
                            }} />
                    }

                    <BotoShowBuscar
                        showBuscar={showBuscar}
                        setShowBuscar={setShowBuscar}
                        setNameSearch={setNameSearch}
                        setIsFocused={setIsFocused}
                    />
                </div>
            </header>

            <div
                className="castellers-container"
                style={{
                    paddingLeft: isDesktop ? 50 : 'auto',
                    paddingRight: isDesktop ? 50 : 'auto',
                    paddingTop: isDesktop ? 20 : 'auto',
                }}
            >
                {
                    // uniqueAssistVals
                    [1]
                    .map(assist => {
                        const assistents = filteredCastellers.filter(c => uniqueAssistVals.some(val => val === c['assistència']));
                        // const assistName =
                        //     assist === null ? 'No confirmats' :
                        //     assist === 2 ? 'Han arribat' :
                        //     assist === 1 ? 'Venen' :
                        //     assist === 0 ? 'No venen' :
                        //     'No confirmats'

                        return assistents.length > 0 && (
                            <div key={uniqueAssistVals} style={{ padding: isBrowser ? 10 : 20 }}>

                                {
                                    (
                                        (props.name === 'etiquetes' && nameSearch === '') ?
                                            etiquetes
                                                .map(({ id }) => etiquetaUsers?.[id])
                                                .filter(etiquetaUsers => etiquetaUsers?.castellers?.length > 0)
                                                // .concat([{
                                                //     perfil: 'Canalla',
                                                //     castellers: filteredCastellers.filter(c => parseInt(c.canalla) !== 0)
                                                // }, {
                                                                    //     perfil: 'Novell',
                                                                    //     castellers: filteredCastellers.filter(c => parseInt(c?.novell) === 1)
                                                // }, {
                                                //     perfil: 'Músic',
                                                //     castellers: filteredCastellers.filter(c => parseInt(c.music) !== 0)
                                                // }])
                                                // Sort by perfil
                                                .sort((a, b) => {
                                                    if (a.perfil === 'Tots') return -1;
                                                    if (b.perfil === 'Tots') return 1;

                                                    return a.perfil.localeCompare(b.perfil);
                                                })
                                            :
                                        (props.name === 'confirmats' && nameSearch === '') ?
                                            getConfirmatsPerfils(filteredCastellers)
                                        :
                                        (props.name === 'canalla' && nameSearch === '') ?
                                            [{
                                                perfil: 'Fitxats',
                                                castellers: filteredCastellers.filter(c => c['assistència'] === 2)
                                            },
                                            {
                                                perfil: 'Estan arribant',
                                                castellers: filteredCastellers.filter(c => c['assistència'] === 1)
                                            },
                                            {
                                                perfil: 'No confirmats',
                                                castellers: filteredCastellers
                                                    .filter(c => c['assistència'] === null)
                                            },
                                            {
                                                perfil: 'No venen',
                                                castellers: filteredCastellers
                                                    .filter(c => c['assistència'] === 0)
                                            }
                                            ]
                                        :
                                        (props.name === 'famílies' && nameSearch === '') ?
                                            [{
                                                perfil: 'Fitxats',
                                                castellers: filteredCastellers.filter(c => c['assistència'] === 2)
                                            },
                                            {
                                                perfil: 'Estan arribant',
                                                castellers: filteredCastellers.filter(c => c['assistència'] === 1)
                                            },
                                            {
                                                perfil: 'No confirmats',
                                                castellers: filteredCastellers
                                                    .filter(c => c['assistència'] === null)
                                            },
                                            {
                                                perfil: 'No venen',
                                                castellers: filteredCastellers
                                                    .filter(c => c['assistència'] === 0)
                                            }
                                            ]
                                        :
                                        (props.name === 'amagats' && nameSearch === '') ?
                                            [{
                                                perfil: 'Fitxats',
                                                castellers: filteredCastellers.filter(c => c['assistència'] === 2)
                                            },
                                            {
                                                perfil: 'Estan arribant',
                                                castellers: filteredCastellers.filter(c => c['assistència'] === 1)
                                            },
                                            {
                                                perfil: 'No confirmats',
                                                castellers: filteredCastellers
                                                    .filter(c => c['assistència'] === null)
                                            },
                                            {
                                                perfil: 'No venen',
                                                castellers: filteredCastellers
                                                    .filter(c => c['assistència'] === 0)
                                            }
                                            ]
                                        :
                                            [{
                                                perfil: 'Tots',
                                                castellers: filteredCastellers
                                                    // .filter(c => !etiquetes.some(e => etiquetaUsers?.[e.id]?.castellers?.some(casteller => casteller.id === c.id)))
                                            }]
                                    )
                                        .map(({ perfil, castellers: castellersInPerfil }) => {
                                            const castellersInPerfilOnAssist = assistents
                                                .filter(casteller => castellersInPerfil.map(u => u.id).includes(casteller.id))

                                            const usersInAssist = castellersInPerfil.filter(user => assistents.map(c => c.id).includes(user.id));
                                            const printedCastellers = castellersInPerfilOnAssist.filter(casteller => casteller.id in props.posicions.castellers);
                                            
                                            // Filter out castellers who arrive late or leave early based on their actual times
                                            const availableCastellers = castellersInPerfilOnAssist.filter(casteller => {
                                                const assistenciaInfo = props.assistenciesEvent?.find(a => parseInt(a.id) === parseInt(casteller.id));
                                                if (!assistenciaInfo || !props.matchedProva) return true;

                                                const arrivalTime = assistenciaInfo['data-entrada'];
                                                const departureTime = assistenciaInfo['data-sortida'];

                                                // Check if arrives late
                                                if (arrivalTime && moment(arrivalTime).isValid() && 
                                                    moment(props.matchedProva.start) < applyTimeZone(arrivalTime)) {
                                                    return false;
                                                }

                                                // Check if leaves early
                                                if (departureTime && moment(departureTime).isValid() && 
                                                    applyTimeZone(departureTime) < moment(props.matchedProva.end)) {
                                                    return false;
                                                }

                                                return true;
                                            });

                                            const castellersQueFaltenLength = availableCastellers.length - printedCastellers.length;

                                            const perfilKey = normalizePerfilKey(props.name, perfil);
                                            const etiquetaHasToBeShown = nameSearch !== '' || !!showEtiqueta?.[perfilKey]

                                            const llistaCastellers = assistents
                                                .filter(casteller => castellersInPerfil.map(u => u.id).includes(casteller.id))
                                                .sort((a, b) => {
                                                    if (nameSearch !== '') {
                                                        return sortMatches(a, nameSearch) > sortMatches(b, nameSearch) ? 1 : -1;
                                                    }

                                                    // if (a['assistència'] < b['assistència']) return 1;
                                                    // if (a['assistència'] > b['assistència']) return -1;
                                                    if (isNaN(a.altura)) return 1;
                                                    if (isNaN(b.altura)) return -1;
                                                    return a.altura - b.altura;
                                                })

                                            const primerDeLaLlista = llistaCastellers?.[0]

                                            return usersInAssist.length > 0 && (
                                                <React.Fragment key={perfil}>
                                                    <Pressable
                                                        onPressColor={'rgba(0,0,0,0.05)'}
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'row',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            cursor: 'pointer', // Makes the div look clickable
                                                            paddingTop: 10,
                                                            paddingBottom: 10,
                                                            backgroundColor: 'transparent',
                                                        }}
                                                        onClick={() => setShowEtiqueta(prevState => togglePerfilVisibility(props.name, prevState, perfil))}
                                                    >
                                                        <div>
                                                            {/* Replace button with an arrow icon */}
                                                            {etiquetaHasToBeShown ? '↓' : '→'} {/* Unicode arrows, or use an icon library */}
                                                        </div>

                                                        <div>
                                                            {
                                                                perfil !== 'Tots' ? <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                    {/* <span>Amb l'etiqueta </span> */}
                                                                    <strong className="assist">
                                                                        {perfil?.toUpperCase()}
                                                                    </strong>
                                                                    <span
                                                                        style={{
                                                                            padding: 5,
                                                                            borderRadius: 5,
                                                                            backgroundColor: SETTINGS(COLLA).color,
                                                                            color: 'white',
                                                                            marginLeft: 10,
                                                                            fontSize: 14,
                                                                        }}
                                                                    >
                                                                        {
                                                                            castellersQueFaltenLength > 0 ? castellersQueFaltenLength :
                                                                            <>✓</>
                                                                        }
                                                                    </span>
                                                                </div> : <>
                                                                    <span>Tots els castellers</span>
                                                                    <span
                                                                        style={{
                                                                            padding: 5,
                                                                            borderRadius: 5,
                                                                            backgroundColor: SETTINGS(COLLA).color,
                                                                            color: 'white',
                                                                            marginLeft: 10,
                                                                            fontSize: 14,
                                                                        }}
                                                                    >
                                                                        {
                                                                            castellersQueFaltenLength > 0 ? castellersQueFaltenLength :
                                                                            <>✓</>
                                                                        }
                                                                    </span>
                                                                </>
                                                            }
                                                        </div>
                                                    </Pressable>

                                                    {
                                                        etiquetaHasToBeShown && <div>
                                                            {
                                                                nameSearch !== '' &&
                                                                <div
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        flexDirection: 'column',
                                                                        margin: 10,
                                                                    }}
                                                                >
                                                                    <Casteller
                                                                        key={primerDeLaLlista.id}
                                                                        isCanalla={!!parseInt(primerDeLaLlista.canalla)}
                                                                        isNovell={parseInt(primerDeLaLlista?.novell) === 1}
                                                                        altura={isNaN(primerDeLaLlista.altura) ? null : primerDeLaLlista.altura}
                                                                        altura_mans={props.castellersInfo[primerDeLaLlista.id]?.altura_mans}
                                                                        selected={props.castellerSelected === primerDeLaLlista.id}
                                                                        printed={primerDeLaLlista.id in props.posicions.castellers}
                                                                        panBack={setPanBackToSelected}
                                                                        arribaTard={arribenTard.includes(primerDeLaLlista.id)}
                                                                        surtAviat={surtenAviat.includes(primerDeLaLlista.id)}
                                                                        matched={true}
                                                                        {...primerDeLaLlista}
                                                                        {...props}
                                                                    />
                                                                </div>
                                                            }
                                                            <div className="castellers">
                                                                {
                                                                    llistaCastellers
                                                                        .filter(casteller => nameSearch !== '' ? casteller.id !== primerDeLaLlista.id : true)
                                                                        .map(casteller =>
                                                                            <Casteller
                                                                                key={casteller.id}
                                                                                isCanalla={!!parseInt(casteller.canalla)}
                                                                                isNovell={parseInt(casteller?.novell) === 1}
                                                                                altura={isNaN(casteller.altura) ? null : casteller.altura}
                                                                                altura_mans={props.castellersInfo[casteller.id]?.altura_mans}
                                                                                selected={props.castellerSelected === casteller.id}
                                                                                printed={casteller.id in props.posicions.castellers}
                                                                                panBack={setPanBackToSelected}
                                                                                arribaTard={arribenTard.includes(casteller.id)}
                                                                                surtAviat={surtenAviat.includes(casteller.id)}
                                                                                assist={casteller['assistència']}
                                                                                {...casteller}
                                                                                {...props}
                                                                            />
                                                                        )
                                                                }
                                                            </div>
                                                        </div>
                                                    }

                                                        {/* {
                                                            assistents
                                                                .filter(casteller => castellers.map(u => u.id).includes(casteller.id))
                                                                .length === 0 && <div style={{ margin: 10 }}>No hi ha cap casteller a <strong>{assistName?.toUpperCase()}</strong> amb aquesta etiqueta</div>
                                                        } */}

                                                    {nameSearch === '' && <hr />}
                                                </React.Fragment>
                                            )
                                        })
                                }
                            </div>
                        )
                    })
                }

                {
                    !isModel && showBuscar && nameSearch.trim() && props.castellers.length === 0 &&
                        <p className="temporary-person-no-matches">No hi ha coincidències</p>
                }
            </div>
        </>
    );
}

export default LlistaCastellers;
